import { FC, PropsWithChildren } from 'react'

import { pluralize } from '@/helpers/text.utils'
import { TQuestion } from '@/redux/services/webinarsApi'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { getTimeAsString } from './Quiz.model'
import s from './Quiz.module.scss'
import { TExcludeQuestionAnswer } from './QuizPopup'

interface IContainerProps extends PropsWithChildren {
    isFinish: boolean
    onClose: () => void
}

export const Container: FC<IContainerProps> = ({ children, isFinish, onClose }) => {
    return (
        <div
            className={s.quizPopup}
            onMouseDown={onClose}
        >
            <div
                onMouseDown={e => e.stopPropagation()}
                className={classNames(s.quizPopup__inner, {
                    [s.quizPopup__inner_finish]: isFinish
                })}
            >
                <IconButton
                    className={s.quizPopup__cross}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>

                {children}
            </div>
        </div>
    )
}

type TQuestionHeaderProps = {
    progress: number
    currentQuestion: number
    questionsCount: number
}

export const QuestionHeader: FC<TQuestionHeaderProps> = ({ progress, currentQuestion, questionsCount }) => {
    return (
        <>
            <div className={s.progressBar}>
                <motion.div
                    className={s.progressBar__line}
                    style={{
                        width: `${progress}%`
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5 }}
                />
            </div>
            <p className={classNames(s.quizPopup__questionNumber, 'categories-slider__text')}>
                Вопрос {currentQuestion} из {questionsCount}
            </p>
        </>
    )
}

type TQuestionProps = {
    question: TQuestion
    onCheckboxChange: (answer: TExcludeQuestionAnswer) => void
}

export const Question: FC<TQuestionProps> = ({ question, onCheckboxChange }) => {
    const { control, getValues, setValue, watch } = useForm<{
        items: { checked: boolean; value: string }[]
    }>({
        defaultValues: {
            items: question.answer_options.map(value => ({
                checked: false,
                value: value
            }))
        }
    })

    

    const { fields } = useFieldArray({
        control,
        name: 'items'
    })

    const values = watch('items')

    const handleCheckboxChange = (index: number, checked: boolean) => {
        if (question.type === 'VARIANTS') {
            fields.forEach((_, i) => {
                if (i === index) {
                    setValue(`items.${index}.checked`, checked)
                    return
                }

                setValue(`items.${i}.checked`, false)
            })
        } else {
            setValue(`items.${index}.checked`, checked)
        }

        onCheckboxChange(getAnswer())
    }

    const getAnswer = () => {
        const answers = values.filter(item => item.checked).map(item => item.value)
        return {
            question: question.id,
            text: question.type === 'VARIANTS' ? answers[0] : '',
            multi_text: question.type === 'MULTIPLE_VARIANTS' ? answers : []
        }
    }

    return (
        <div className={s.questions}>
            <p className={s.questions__title}>{question.title}</p>
            <ul className={s.questions__list}>
                {fields.map((field, index) => {
                    return (
                        <li
                            key={field.id}
                            className={classNames(s.checkbox, {
                                [s.checkbox_inactive]: false
                            })}
                        >
                            <Controller
                                control={control}
                                name={`items.${index}`}
                                render={({ field }) => (
                                    <label>
                                        <input
                                            type='checkbox'
                                            checked={values[index].checked}
                                            onChange={event => {
                                                
                                                // field.onChange({
                                                //     ...field.value,
                                                //     checked: event.target.checked
                                                // })

                                                handleCheckboxChange(index, event.target.checked)
                                            }}
                                        />
                                        <span />
                                        <p>{question.answer_options[index]}</p>
                                    </label>
                                )}
                            />
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

type TTitle = {
    title: string
}

export const Title: FC<TTitle> = ({ title }) => {
    return <h3 className={s.quizPopup__title}>{title}</h3>
}

type TIntroProps = {
    questionsCount: number
    passingScore: number
    minutes: number
    seconds: number
}

export const Intro: FC<TIntroProps> = ({ questionsCount, passingScore, minutes, seconds }) => {
    return (
        <>
            <p className={s.quizPopup__descr}>
                Ответьте на все {questionsCount} {pluralize(questionsCount, 'вопрос', 'вопроса', 'вопросов')}, выбрав
                один из предложенных <br /> вариантов. Если вы ответите на {Math.round(passingScore / 2)} {pluralize(Math.round(passingScore / 2), 'вопрос', 'вопроса', 'вопросов')} правильно, вы <br /> получите
                полезный подарок от нашей компании. Удачи!
            </p>
            <div className={s.quizPopup__important}>
                <ul className={s.quizPopup__importantList}>
                    <li className={classNames(s.quizPopup__importantItem, s.quizPopup__importantItem_title)}>
                        Важно знать:
                    </li>
                    <li className={s.quizPopup__importantItem}>
                        — необходимо ответить на {Math.round(passingScore / 2)}{' '}
                        {pluralize(Math.round(passingScore / 2), 'вопрос', 'вопроса', 'вопросов')} из&nbsp;
                        {questionsCount} правильно
                    </li>
                    <li className={s.quizPopup__importantItem}>
                        — на выполнение заданий выделено {getTimeAsString(minutes, seconds)}
                    </li>
                    <li className={s.quizPopup__importantItem}>— задание можно пройти один раз</li>
                </ul>
            </div>
        </>
    )
}
